/* ==========================================================================
   iTechVista — collection grid (progressive enhancement)
   --------------------------------------------------------------------------
   sections/itv-collection-grid.liquid works completely without this file: the
   facets and the sort control are one <form method="get">, and its "Apply"
   button navigates to the filtered URL. This script only makes that same
   navigation feel quicker. It never invents behaviour the form does not have.

   What it does:
     1. Flags the section with data-enhanced, which is the ONLY reason the
        Apply button disappears (see the stylesheet). If this file fails to
        load, or the browser lacks fetch / DOMParser / pushState, the flag is
        never set and the button stays visible and functional.
     2. Submits the form on change instead of on click, then fetches the URL
        the form would have gone to and swaps the two regions marked
        [data-itv-cg-controls] and [data-itv-cg-results].
     3. Falls back to a real window.location navigation to that identical URL
        on any error, so a failed fetch is invisible to the visitor.

   Deliberately NOT handled here: the quick-add forms. They are ordinary POSTs
   to Shopify's cart-add route and are left alone, because an intercepted add
   with no cart UI to update would be a lie about what happened.
   ========================================================================== */

(function () {
  'use strict';

  var SUPPORTED =
    typeof window.fetch === 'function' &&
    typeof window.DOMParser === 'function' &&
    typeof window.URLSearchParams === 'function' &&
    typeof window.FormData === 'function' &&
    !!(window.customElements && window.customElements.define) &&
    !!(window.history && window.history.pushState);

  var CONTROLS = '[data-itv-cg-controls]';
  var RESULTS = '[data-itv-cg-results]';
  var FORM = '[data-itv-cg-form]';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var ItvCollectionGrid = class extends HTMLElement {
    connectedCallback() {
      if (!SUPPORTED || this.enhanced) return;
      this.enhanced = true;
      this.sectionId = this.dataset.sectionId || '';
      this.requestToken = 0;
      this.debounceTimer = null;

      this.handleChange = this.onChange.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);
      this.handleClick = this.onClick.bind(this);
      this.handlePopState = this.onPopState.bind(this);

      // Delegated from the section root, which survives every region swap.
      this.addEventListener('change', this.handleChange);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('click', this.handleClick);
      window.addEventListener('popstate', this.handlePopState);

      this.setAttribute('data-enhanced', 'true');
    }

    disconnectedCallback() {
      if (this.handlePopState) window.removeEventListener('popstate', this.handlePopState);
      window.clearTimeout(this.debounceTimer);
    }

    facetForm() {
      return this.querySelector(FORM);
    }

    onChange(event) {
      var form = this.facetForm();
      if (!form || !form.contains(event.target)) return;

      // Coalesce a burst of changes (a typed price, tabbing through boxes).
      window.clearTimeout(this.debounceTimer);
      var url = this.formUrl(form);
      var self = this;
      this.debounceTimer = window.setTimeout(function () {
        self.render(url, { push: true });
      }, 150);
    }

    onSubmit(event) {
      var form = event.target;
      // Quick-add forms must POST normally; only the facet form is hijacked.
      if (!form || !form.matches || !form.matches(FORM)) return;
      event.preventDefault();
      window.clearTimeout(this.debounceTimer);
      this.render(this.formUrl(form), { push: true });
    }

    onClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = event.target.closest ? event.target.closest('a[data-itv-cg-link]') : null;
      if (!link || !this.contains(link)) return;

      event.preventDefault();
      this.render(link.href, {
        push: true,
        scroll: link.hasAttribute('data-itv-cg-scroll'),
      });
    }

    onPopState() {
      if (!this.isConnected) return;
      this.render(window.location.href, { push: false });
    }

    /* The exact URL a plain submit of this form would have produced. */
    formUrl(form) {
      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        if (typeof value === 'string' && value.trim() !== '') params.append(key, value);
      });
      var action = form.getAttribute('action') || window.location.pathname;
      var query = params.toString();
      return query ? action + '?' + query : action;
    }

    matchingSection(doc) {
      var candidates = doc.querySelectorAll('[data-itv-cg]');
      for (var i = 0; i < candidates.length; i += 1) {
        if (!this.sectionId || candidates[i].getAttribute('data-section-id') === this.sectionId) {
          return candidates[i];
        }
      }
      return candidates[0] || null;
    }

    render(url, options) {
      var opts = options || {};
      var self = this;

      if (!this.querySelector(RESULTS)) {
        window.location.assign(url);
        return;
      }

      var token = (this.requestToken += 1);
      this.setAttribute('aria-busy', 'true');

      window
        .fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          // A newer request has already been fired; let that one finish.
          if (token !== self.requestToken) return;

          var next = self.matchingSection(new DOMParser().parseFromString(html, 'text/html'));
          if (!next) throw new Error('Collection grid missing from response');

          self.swap(next);
          if (opts.push) window.history.pushState({ itvCg: self.sectionId }, '', url);
          if (opts.scroll) {
            self.scrollIntoView({
              behavior: prefersReducedMotion() ? 'auto' : 'smooth',
              block: 'start',
            });
          }
          self.removeAttribute('aria-busy');
        })
        .catch(function () {
          // Honest fallback: go where the form was going anyway.
          window.location.assign(url);
        });
    }

    swap(next) {
      // Which disclosures were open, and where the keyboard was, before the
      // markup underneath them is replaced.
      var openFacets = [];
      this.querySelectorAll('[data-itv-cg-facet][open]').forEach(function (facet) {
        openFacets.push(facet.getAttribute('data-itv-cg-facet'));
      });

      var active = document.activeElement;
      var focusId = active && active.id && this.contains(active) ? active.id : null;

      [CONTROLS, RESULTS].forEach(function (selector) {
        var current = this.querySelector(selector);
        var incoming = next.querySelector(selector);
        if (current && incoming) current.innerHTML = incoming.innerHTML;
      }, this);

      openFacets.forEach(function (key) {
        var facet = this.querySelector('[data-itv-cg-facet="' + key + '"]');
        if (facet) facet.open = true;
      }, this);

      if (focusId) {
        var restored = document.getElementById(focusId);
        if (restored && typeof restored.focus === 'function') {
          restored.focus({ preventScroll: true });
        }
      }
    }
  };

  if (SUPPORTED && !window.customElements.get('itv-collection-grid')) {
    window.customElements.define('itv-collection-grid', ItvCollectionGrid);
  }
})();
